import { ENoviDashboardSider } from '@/pages/Novi/enums/ENoviDashboardSider';
import { setCurrentPage, setSelectedAgent } from '@/store/account/slice';
import {
	BarChartOutlined,
	CheckCircleOutlined,
	CloseCircleOutlined,
	CloseOutlined,
	HistoryOutlined,
	MessageOutlined,
	PhoneOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import { PeopleOutline } from '@mui/icons-material';
import { Avatar, Button, Col, Modal, notification, Row, Spin, Tag, Typography } from 'antd';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import AddNewLeads from '../Leads/AddLeads/AddNewLeads';
import { getLeadCountsByStatus, getLeads } from '@/api/novi/leads';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { updateCampaignStatus } from '@/api/novi/campaigns';
import { getLoggedInUser, getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import { useAgents } from 'src/Hooks/useAgents';
import { getSchedule, updateScheduleStatus } from '@/api/novi/schedule';

const { Title, Text } = Typography;

type LeadCategory = 'NEW' | 'CONTACTED' | 'PROCESSING' | 'FAILED';

const PAGE_SIZE = 100;

interface LeadResponse {
	leads: any[];
	pagination: {
		nextKey: string | null;
		hasMore: boolean;
	};
	leadStats?: {
		total: string;
		daily: string;
		totalRemaining: number;
		dailyRemaining: number;
	};
}

const CampaignDashboard: React.FC = () => {
	const dispatch = useDispatch();
	const noviUser = useAppSelector(getLoggedInUser);
	const campaignId = useAppSelector(getSelectedAgentId) as string;
	const selectedCampaign = useAppSelector(getSelectedAgent);
	const queryClient = useQueryClient();
	const { refetchAgents, outboundAgents } = useAgents();

	const [addLeadModal, setAddLeadModal] = useState(false);
	const [savingStatus, setSavingStatus] = useState(false);
	const [isToggling, setIsToggling] = useState(false);

	const [leadStats, setLeadStats] = useState({
		total: '0/0',
		daily: '0/0',
		totalRemaining: 0,
		dailyRemaining: 0,
	});

	const [leadsData, setLeadsData] = useState<Record<string, any>>({
		NEW: {
			leads: [],
			hasMore: true,
			nextKey: null,
			loading: false,
		},
		CONTACTED: {
			leads: [],
			hasMore: true,
			nextKey: null,
			loading: false,
		},
		PROCESSING: {
			leads: [],
			hasMore: true,
			nextKey: null,
			loading: false,
		},
		FAILED: {
			leads: [],
			hasMore: true,
			nextKey: null,
			loading: false,
		},
	});

	const { data: statusCounts, isPending } = useQuery({
		queryKey: ['lead-counts', campaignId],
		queryFn: () => getLeadCountsByStatus(campaignId),
		enabled: !!campaignId,
	});

	const {
		data: campaignSchedule,
		isLoading: scheduleLoading,
		refetch: refetchSchedule,
	} = useQuery({
		queryKey: ['campaign-schedule', campaignId],
		queryFn: () => getSchedule(campaignId as string),
		enabled: !!campaignId,
		retry: 2,
		staleTime: 30000,
	});

	const { mutate: scheduleStatusUpdate } = useMutation({
		mutationFn: async (params: { campaign_id: string; status: string }) => {
			setIsToggling(true);
			try {
				await updateScheduleStatus(params);
			} finally {
				setIsToggling(false);
			}
		},
		onSuccess: () => {
			notification.success({
				message: 'Schedule Status Updated',
				description: 'The schedule status has been updated successfully.',
				duration: 3,
			});
			refetchSchedule();
		},
		onError: (error: Error) => {
			notification.error({
				message: 'Failed to Update Schedule Status',
				description: error.message || 'An error occurred while updating the schedule status.',
				duration: 4,
			});
			refetchSchedule();
		},
	});

	const fetchLeadsByStatus = useCallback(
		async (status: string, nextKey?: string | null) => {
			setLeadsData((prev) => ({
				...prev,
				[status]: { ...prev[status], loading: true },
			}));

			try {
				const response: LeadResponse = await getLeads(campaignId, {
					status,
					nextKey: nextKey || '',
					limit: PAGE_SIZE,
					phoneNumber: '',
				});

				if (response.leadStats) {
					setLeadStats(response.leadStats);
				}

				setLeadsData((prev) => ({
					...prev,
					[status]: {
						...prev[status],
						leads: nextKey ? [...prev[status].leads, ...response.leads] : [...response.leads],
						hasMore: response.pagination.hasMore,
						nextKey: response.pagination.nextKey || null,
						loading: false,
					},
				}));
			} catch (error) {
				setLeadsData((prev) => ({
					...prev,
					[status]: { ...prev[status], loading: false },
				}));
			}
		},
		[campaignId],
	);

	useQuery({
		queryKey: ['leads', campaignId],
		queryFn: async () => {
			await Promise.all(Object.keys(leadsData).map((status) => fetchLeadsByStatus(status)));
		},
		enabled: !!campaignId,
	});

	const handleStatusToggle = async () => {
		if (isToggling || !campaignSchedule) return;
		const newStatus =
			campaignSchedule.schedule?.status?.toLowerCase() === 'active' ? 'INACTIVE' : 'ACTIVE';
		scheduleStatusUpdate({
			campaign_id: campaignId || '',
			status: newStatus,
		});
	};

	const handleLeads = () => {
		dispatch(setCurrentPage(ENoviDashboardSider.LEADS));
	};

	const closeAddLeadModal = () => setAddLeadModal(false);

	const statuses: LeadCategory[] = ['NEW', 'CONTACTED', 'PROCESSING', 'FAILED'];

	const getCategoryIcon = (category: LeadCategory) => {
		switch (category) {
			case 'NEW':
				return <HistoryOutlined className="text-yellow-500" />;
			case 'CONTACTED':
				return <CheckCircleOutlined className="text-green-500" />;
			case 'PROCESSING':
				return <HistoryOutlined className="text-blue-500" />;
			case 'FAILED':
				return <CloseCircleOutlined className="text-red-500" />;
		}
	};

	const isScheduleActive = campaignSchedule?.schedule?.status?.toLowerCase() === 'active';

	return (
		<div className="w-full bg-gray-50">
			<div className="bg-white p-6 flex justify-between items-center rounded-lg flex-wrap">
				<h1 className="text-xl font-bold">{selectedCampaign?.name}</h1>
			</div>

			<div className="p-6">
				<div className="bg-white p-6 rounded-lg shadow-sm mb-6">
					<div className="flex items-center justify-between flex-wrap">
						<div className="flex items-center flex-wrap gap-4">
							<Avatar className="bg-blue-100 text-blue-500" size={60}>
								<PhoneOutlined className="text-3xl rotate-90" />
							</Avatar>
							<div>
								<div className="flex items-center flex-wrap">
									<h2 className="text-lg font-semibold mb-1">{selectedCampaign?.name}</h2>
									<Tag
										color={
											selectedCampaign?.campaignOnboardingSteps?.length &&
											selectedCampaign?.campaignOnboardingSteps?.length > 4
												? 'green'
												: selectedCampaign?.status === 'warning'
													? 'orange'
													: 'red'
										}
										className="ml-2 rounded-full font-semibold"
									>
										{selectedCampaign?.campaignOnboardingSteps?.length &&
										selectedCampaign?.campaignOnboardingSteps?.length > 4
											? 'Healthy'
											: selectedCampaign?.status === 'warning'
												? 'Needs Attention'
												: 'Complete Onboarding'}
									</Tag>
								</div>
								<p className="text-gray-500 mb-1">
									Campaign is {isScheduleActive ? 'Active' : 'Paused'}
								</p>
								<p className="font-medium capitalize">
									{selectedCampaign?.communicationType
										?.replace(/-/g, ' ')
										.replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'NA'}
								</p>
							</div>
						</div>
						<Button
							className={`${isScheduleActive ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-500 hover:bg-green-600'} text-white rounded-md px-4 py-2 border-0`}
							size="large"
							disabled={
								noviUser.outboundDemo ||
								!noviUser.outboundSubscriptionId ||
								!noviUser.outboundSubscriptionStatus ||
								scheduleLoading
							}
							onClick={handleStatusToggle}
							loading={isToggling}
						>
							{isScheduleActive ? 'Pause Campaign' : 'Start Campaign'}
						</Button>
					</div>
				</div>

				{isPending ? (
					<Spin />
				) : (
					<>
						<div className="mb-6">
							<div className="bg-white p-3">
								<div className="flex justify-between items-center">
									<h3 className="text-gray-500">Total Leads</h3>
									<PeopleOutline className="text-blue-400" />
								</div>
								<div className="flex justify-between items-center">
									<h2 className="text-3xl font-bold">{statusCounts?.statusCounts?.TOTAL ?? 0}</h2>
									<Button
										onClick={() => setAddLeadModal(true)}
										type="primary"
										icon={<PlusOutlined />}
									>
										Add More Leads
									</Button>
								</div>
							</div>
						</div>

						<Row gutter={16} className="mb-6">
							{statuses.map((category) => (
								<Col lg={8} md={12} sm={24} xs={24} key={category} className="mb-4">
									<div className="bg-white p-4 rounded-lg shadow-sm">
										<div className="flex justify-between items-center">
											<h3 className="text-gray-500 capitalize mb-2">{category}</h3>
											{getCategoryIcon(category)}
										</div>
										<p className="text-3xl font-bold">
											{statusCounts?.statusCounts?.[category] ?? 0}
										</p>
										<a onClick={handleLeads} className="text-blue-500 flex items-center gap-2 mt-2">
											<PeopleOutline fontSize="small" />
											View Leads
										</a>
									</div>
								</Col>
							))}
						</Row>
					</>
				)}

				<div className="bg-white rounded-lg shadow-sm">
					<div className="flex justify-between items-center mb-4 border-b p-4">
						<h2 className="text-lg font-semibold flex items-center">
							<BarChartOutlined className="text-blue-600 mr-2" />
							Campaign Overview
						</h2>
					</div>
					<div className="grid grid-cols-2 gap-6 p-4">
						<div>
							<h3 className="text-gray-500 mb-2">Campaign Details</h3>
							<p>
								<b>Goal:</b> {selectedCampaign?.campaignGoal ?? 'NA'}
							</p>
							<p>
								<b>Lead Source:</b> {selectedCampaign?.leadSource ?? 'NA'}
							</p>
							<p>
								<b>Communication Type:</b>{' '}
								{selectedCampaign?.communicationType
									?.replace(/-/g, ' ')
									.replace(/\b\w/g, (c: string) => c.toUpperCase()) ?? 'NA'}
							</p>
						</div>
					</div>
				</div>
			</div>

			<Modal
				open={addLeadModal}
				onCancel={closeAddLeadModal}
				footer={null}
				width={800}
				closeIcon={<CloseOutlined />}
				title={
					<div style={{ padding: '12px 0' }}>
						<Title level={4} style={{ margin: 0 }}>
							Add New Leads
						</Title>
						<Text type="secondary">Choose how you'd like to import your leads</Text>
					</div>
				}
			>
				<AddNewLeads closeModal={closeAddLeadModal} refetchLeads={fetchLeadsByStatus} />
			</Modal>
		</div>
	);
};

export default CampaignDashboard;
